import { Box } from 'components/Material/Box'
import { usePopup } from 'contexts/PopupContext'
import { getPubliList } from 'services/publicity'
import { useMutation } from '@tanstack/react-query'
import { ErrorResponse } from 'utils/HandlerErrorMessage'
import { AxiosError } from 'axios'
import { TODAY_DATE } from 'utils/variablesAndSelects'
import { useEffect, useState } from 'react'
import { Progress } from 'components/Custom/Progress'
import { useIsMobile } from 'hooks/useIsResponsive'

import RenderImage from 'utils/fileFunction'
import { ImageType, PubliCardType } from 'types/publicidade'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import IconButton from 'components/Material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

const PublicidadeAlert = () => {
  const { popup, hidePopup } = usePopup()
  const isMobile = useIsMobile()
  const [publiRender, setPubliRender] = useState<ImageType | null>(null)
  const [nomeCampanha, setNomeCampanha] = useState<string | null>(null)
  const oneYerAgo = new Date()
  oneYerAgo.setFullYear(oneYerAgo.getFullYear() - 1)
  const getPubliListMutation = useMutation({
    mutationFn: getPubliList,
    onError: (error: AxiosError<ErrorResponse>) => {
      console.error('getPubliList', error)
      hidePopup()
    },
    onSuccess: (data) => {
      if (data.length === 0) {
        hidePopup()
      } else {
        handleSelectPubli(data)
      }
    }
  })

  const {
    data: getPubliCardsList,
    mutate: callGetPubliList,
    isPending,
    isPaused
  } = getPubliListMutation

  useEffect(() => {
    if (isPaused) {
      hidePopup()
    }
  }, [isPaused])

  function normalizeToDate(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  const handleSelectPubli = (publi: PubliCardType[]) => {
    if (publi.length === 0) {
      hidePopup()
      return
    }

    const currentDate = normalizeToDate(TODAY_DATE)
    const activeCampaigns = publi.filter((publiCard) => {
      const startDate = normalizeToDate(new Date(publiCard.dt_iv_campanha))
      const endDate = normalizeToDate(new Date(publiCard.dt_fv_campanha))
      return startDate <= currentDate && endDate >= currentDate
    })

    if (activeCampaigns.length === 0) {
      hidePopup()
      return
    }

    activeCampaigns.sort(
      (a, b) =>
        normalizeToDate(new Date(a.dt_fv_campanha)).getTime() -
        normalizeToDate(new Date(b.dt_fv_campanha)).getTime()
    )

    const selectedCampaign = activeCampaigns[0]

    setNomeCampanha(selectedCampaign.nm_campanha)

    if (isMobile && selectedCampaign.id_corcc080_mobile !== 0) {
      setPubliRender(selectedCampaign.imagem_mobile)
    } else if (!isMobile && selectedCampaign.id_corcc080_desktop !== 0) {
      setPubliRender(selectedCampaign.imagem_desktop)
    } else {
      hidePopup()
    }
  }

  const getPubliCards = () => {
    const defaultFilter = {
      dt_iv_campanha: oneYerAgo,
      st_status: 'A',
      nm_campanha: '',
      nm_menu: popup.content ?? undefined
    }
    callGetPubliList(defaultFilter)
  }

  useEffect(() => {
    if (popup.isOpen) {
      getPubliCards()
    } else {
      setPubliRender(null)
    }
  }, [popup.isOpen])

  if (isPending || !getPubliCardsList || !publiRender) {
    return (
      <Dialog open={popup.isOpen} maxWidth="lg" sx={{ padding: 0, margin: 0 }}>
        <Box className="flex justify-center items-center w-[300px] p-5">
          <Progress />
        </Box>
      </Dialog>
    )
  }

  return (
    <Dialog open={popup.isOpen} onClick={hidePopup} maxWidth="lg" sx={{ padding: 0, margin: 0 }}>
      <DialogTitle>{nomeCampanha ?? 'Carregando..'}</DialogTitle>
      <IconButton
        data-testid="publi-close-button"
        aria-label="close"
        onClick={hidePopup}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers style={{ textAlign: 'center', padding: 0, margin: 0 }}>
        {publiRender && (
          <Box className=" items-center flex justify-center">
            <RenderImage
              base64Data={publiRender.documento}
              type={publiRender?.nm_tipo_arquivo}
              isMobile={isMobile}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PublicidadeAlert
